import ErrorBoundary from './ErrorBoundary'
import { RouteProps } from 'react-router-dom'
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toast as toast } from 'components/ui'
import { UserSessionProvider } from 'context/UserSession/UserSessionProvider'
import { useCallback } from 'react'
import strings from 'l10n'
import useUserSession from 'hooks/useUserSession'
import AppContainer from 'app/EntryPoint/AppContainer'
import { LicenseInfo } from '@mui/x-data-grid-premium'

const REACT_APP_MUI_LICENSE_KEY = process.env.REACT_APP_MUI_LICENSE_KEY
LicenseInfo.setLicenseKey(REACT_APP_MUI_LICENSE_KEY || '')

const queryClient = new QueryClient()

// Type validator function example
function isError(args: unknown): args is unknown {
  return true
}

export function errorMsg(error: any, def = strings.WS_ERROR) {
  let message = error?.response?.data?.message?.message
  if (!message) message = error?.response?.data?.message
  return message ?? def
}

function showToast(error: unknown) {
  toast({
    type: 'error',
    subTitle: errorMsg(error),
  })
}

function onErrorHandler(error: unknown) {
  if (isError(error)) {
    console.log('onErrorHandler')
    showToast(error)
    return Promise.reject(error)
  }
  return error
}

const QueryConfig = () => {
  const queryClient = useQueryClient()
  const { logout } = useUserSession()

  const retryHandler = useCallback(
    (failureCount: number, error: unknown) => {
      console.log('retryHandler', failureCount, error)
      if (isError(error)) {
        if ((error as any).response?.status === 401) {
          logout({
            type: 'error',
            subTitle: errorMsg(error, strings.INVALID_SESSION),
          })
          return false
        } else if ((error as any).response?.status === 403) {
          return false
        }
        return failureCount < 3
      }
      return true
    },
    [logout]
  )

  queryClient.setDefaultOptions({
    queries: {
      retry: retryHandler,
      onError: onErrorHandler,
    },
    mutations: {
      retry: false,
      onError: onErrorHandler,
    },
  })

  return <></>
}

const App: React.FunctionComponent<RouteProps> = () => {
  return (
    <ErrorBoundary>
      <UserSessionProvider>
        <QueryClientProvider client={queryClient}>
          <AppContainer />
          <ReactQueryDevtools initialIsOpen={false} position="top-left" />
          <QueryConfig />
        </QueryClientProvider>
      </UserSessionProvider>
    </ErrorBoundary>
  )
}

export default App
